import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils";
function useModalAnimation(isOpen, duration = 300) {
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [isAnimating, setIsAnimating] = useState(isOpen);
    const timeoutRef = useRef(null);
    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            requestAnimationFrame(() => setIsAnimating(true));
            if (timeoutRef.current)
                window.clearTimeout(timeoutRef.current);
            return;
        }
        setIsAnimating(false);
        timeoutRef.current = window.setTimeout(() => setShouldRender(false), duration);
        return () => {
            if (timeoutRef.current)
                window.clearTimeout(timeoutRef.current);
        };
    }, [isOpen, duration]);
    return { shouldRender, isAnimating };
}
const ModalContext = React.createContext(null);
function useModalContext() {
    const ctx = React.useContext(ModalContext);
    if (!ctx)
        throw new Error("Modal components must be used within <Modal>");
    return ctx;
}
function ModalRoot({ open, onOpenChange, children }) {
    const isControlled = open !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(Boolean(open));
    const currentOpen = isControlled ? Boolean(open) : uncontrolledOpen;
    const setOpen = useCallback((next) => {
        if (!isControlled) {
            setUncontrolledOpen(next);
        }
        onOpenChange?.(next);
    }, [isControlled, onOpenChange]);
    useEffect(() => {
        if (currentOpen) {
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [currentOpen]);
    const contextValue = useMemo(() => ({ isOpen: currentOpen, setOpen }), [currentOpen, setOpen]);
    return (_jsx(ModalContext.Provider, { value: contextValue, children: children }));
}
function createModalButton(action) {
    return function ModalButton({ onClick, ...props }) {
        const { setOpen } = useModalContext();
        return (_jsx("button", { type: "button", ...props, onClick: (e) => {
                onClick?.(e);
                setOpen(action);
            } }));
    };
}
const ModalTrigger = createModalButton(true);
const ModalClose = createModalButton(false);
function ModalPortal({ children }) {
    const { isOpen } = useModalContext();
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted || !isOpen)
        return null;
    return createPortal(children, document.body);
}
function ModalOverlay({ className, ...props }) {
    const { isOpen, setOpen } = useModalContext();
    const { shouldRender, isAnimating } = useModalAnimation(isOpen);
    if (!shouldRender)
        return null;
    return (_jsx("div", { "aria-hidden": "true", className: cn("fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-300", isAnimating ? "opacity-100" : "opacity-0", className), onClick: () => setOpen(false), ...props }));
}
function ModalContent({ className, children, ...props }) {
    const { isOpen, setOpen } = useModalContext();
    const { shouldRender, isAnimating } = useModalAnimation(isOpen);
    useEffect(() => {
        if (!isOpen)
            return;
        const onKeyDown = (e) => {
            if (e.key === "Escape")
                setOpen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, setOpen]);
    if (!shouldRender)
        return null;
    return (_jsxs("div", { className: cn("fixed inset-0 z-50 flex items-center justify-center"), children: [_jsx("button", { className: cn("absolute inset-0 bg-black/50", isAnimating
                    ? "opacity-100"
                    : "opacity-0 transition-opacity duration-300"), onClick: () => setOpen(false), "aria-label": "Fechar modal" }), _jsx("div", { className: cn("relative w-full max-w-3xl transform rounded-lg bg-white shadow-xl transition-transform duration-300", isAnimating ? "scale-100" : "scale-95", className), ...props, children: children })] }));
}
function ModalTitle({ className, children, ...props }) {
    return (_jsx("h2", { className: cn("px-5 pt-5 text-lg font-semibold", className), ...props, children: children }));
}
function ModalDescription({ className, ...props }) {
    return (_jsx("p", { className: cn("px-5 pb-4 text-sm text-neutral-600", className), ...props }));
}
function ModalHeader({ className, ...props }) {
    return (_jsx("div", { className: cn("flex items-start justify-between gap-4 px-5 pt-5", className), ...props }));
}
function ModalFooter({ className, ...props }) {
    return (_jsx("div", { className: cn("flex items-center justify-end gap-3 px-5 pb-5", className), ...props }));
}
function ModalCloseX({ className, onClick, ...props }) {
    const { setOpen } = useModalContext();
    return (_jsx("button", { type: "button", "aria-label": "Fechar", className: cn("absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100", className), onClick: (e) => {
            onClick?.(e);
            setOpen(false);
        }, ...props, children: _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", fill: "currentColor", className: "h-5 w-5", children: _jsx("path", { fillRule: "evenodd", d: "M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z", clipRule: "evenodd" }) }) }));
}
export const Modal = {
    Root: ModalRoot,
    Trigger: ModalTrigger,
    Close: ModalClose,
    Portal: ModalPortal,
    Overlay: ModalOverlay,
    Content: ModalContent,
    Title: ModalTitle,
    Description: ModalDescription,
    Header: ModalHeader,
    Footer: ModalFooter,
    CloseX: ModalCloseX,
};
